import { pipe, takeWhile, map, concat, emitter, subscribe, switchMap, never } from 'agos';
import { e, mount } from '../libs/observable-hypertext';
import { persists, onDocumentVisibilityChange } from '../libs/observable-operators';
import { interval, timeout } from '../libs/timer';

const prependZero = num => num.toString().length === 1 ? `0${ num }` : `${ num }`;

// convert timestamp string to JS date
// supports diff browser
const toDate = timestamp => {
  // timestamp format - 2021-04-12 20:00:00
  const values = timestamp.split(/[^0-9]/);
  const year = parseInt(values[ 0 ], 10);
  const month = parseInt(values[ 1 ], 10) - 1; // month is zero based subtract 1
  const day = parseInt(values[ 2 ], 10);
  const hours = parseInt(values[ 3 ], 10);
  const minutes = parseInt(values[ 4 ], 10);
  const seconds = parseInt(values[ 5 ], 10);
  return new Date(year, month, day, hours, minutes, seconds);
};

// convert seconds to countdown stamp format
const toCountdownStamp = seconds => {
  const days = Math.floor(seconds / 86400);
  seconds = seconds - (days * 86400);

  const hours = Math.floor(seconds / 3600);
  seconds = seconds - (hours * 3600);

  const minutes = Math.floor(seconds / 60);
  seconds = seconds - (minutes * 60);

  return {
    days: prependZero(days),
    hours: prependZero(hours),
    minutes: prependZero(minutes),
    seconds: prependZero(seconds),
  };
};

// observable function that propagates a countdown stamp
const countdown = timestamp => pipe(
  onDocumentVisibilityChange,
  switchMap(() => {
    // since timer is using raf we need to
    // recalculate countdown when document
    // comes from inactive state

    // no thrown values at document inactive state
    if (document.hidden) {
      return never();
    }

    // time difference in seconds of countdown timestamp and current date
    const td = (toDate(timestamp).getTime() - Date.now()) / 1000;

    // get the ceiled seconds
    const seconds = td < 0 ? 0 : Math.ceil(td);
    // get the ms difference for synchronization offset
    // value should not be less than 60fps
    const msdiff = Math.ceil(td < (1000 / 60) ? (1000 / 60) : ((td - Math.floor(td)) * 1000));

    return concat([
      // timeout synchronize the milliseconds off
      // of the timer on multiple browser or browser tabs
      // return an empty timestamp
      pipe(
        timeout(msdiff),
        map(() => ({ days: '', hours: '', minutes: '', seconds: '' })),
      ),
      pipe(
        interval(1000),
        map(time => time / 1000),
        takeWhile(time => time < seconds, true),
        map(time => seconds - time),
        map(toCountdownStamp)
      ),
    ]);
  })
);

const CountdownClock = countdown$ => Object
  .entries({
    days: pipe(countdown$, map(d => d.days)),
    hours: pipe(countdown$, map(d => d.hours)),
    mins: pipe(countdown$, map(d => d.minutes)),
    secs: pipe(countdown$, map(d => d.seconds)),
  })
  .map(([label, timer]) =>
    e('div', { class: 'countdown-clock__timer' }, {}, [
      e('p', { class: 'countdown-clock__timer__time' }, {}, timer),
      e('span', { class: 'countdown-clock__timer__label' }, {}, label),
    ])
  );

export default function() {
  const countdownClockContainers = document.querySelectorAll('.countdown-clock');
  countdownClockContainers.forEach(container => {
    // // dirty element property attachment
    const [ctrl, sub] = emitter();
    container.countdown$ = sub;

    window.requestAnimationFrame(() => {
      const { dataset: { timestamp } } = container;
      const countdown$ = persists(countdown(timestamp));

      // pipe down values to container.countdown$
      pipe(countdown$, subscribe(ctrl));
      const unmount = mount(container, CountdownClock(countdown$));

      window.addEventListener('unload', () => unmount());
    });
  });
}
